/**
 * /recruitment/retention — the candidate-PII retention/anonymise queue
 * (ADR-0012 D-9). The human-in-the-loop UI for the retention sweep: it lists
 * candidates the sweep surfaced as past their grace window AND not excluded.
 * Hired / mid-pipeline / converted candidates are NEVER eligible and never
 * appear here (the airtight exclusion is enforced server-side).
 *
 * Anonymising is DESTRUCTIVE (scrubs PII, deletes the CV; the funnel row
 * survives). It is gated behind an explicit, reason-required confirmation
 * (ReasonModal) — deliberately not a one-click action.
 *
 * HR-gated; mounted under the admin layout.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { anonymizeCandidate, listPurgeEligible } from '../api/recruitment.js';
import { ApiError } from '../api/client.js';
import { formatDate } from '../lib/format.js';
import { ReasonModal } from '../components/ReasonModal.js';

export const recruitmentRetentionRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/retention',
  component: RecruitmentRetentionPage,
});

interface EligibleCandidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  countryCode: string;
  source: string | null;
  retentionUntil: string | null;
  createdAt: string;
}

function RecruitmentRetentionPage(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['recruitment', 'purge-eligible'], queryFn: () => listPurgeEligible() });
  const [target, setTarget] = useState<EligibleCandidate | null>(null);
  const [error, setError] = useState<string | null>(null);

  const anonymize = useMutation({
    mutationFn: (reason: string) => anonymizeCandidate(target!.id, reason),
    onSuccess: async () => {
      setTarget(null);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['recruitment', 'purge-eligible'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Anonymisation failed.'),
  });

  const candidates = (query.data?.candidates ?? []) as EligibleCandidate[];

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Retention &amp; erasure</h1>
        <p className="text-sm text-gray-400">
          Candidates past their retention window who are <strong>not</strong> hired and
          <strong> not</strong> mid-pipeline. Anonymising scrubs their personal details
          and deletes their CV — the hiring-funnel record itself is kept. This is
          permanent; hired or active candidates never appear here.
        </p>
      </header>

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load the retention queue.</Placeholder>
      ) : candidates.length === 0 ? (
        <Placeholder>Nothing is eligible for anonymisation right now.</Placeholder>
      ) : (
        <div className="space-y-2">
          {candidates.map((c) => (
            <div key={c.id} className="rounded border border-gray-900 p-4 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-white">{c.firstName} {c.lastName} <span className="text-gray-500 text-xs ml-1">{c.countryCode}</span></p>
                <p className="text-xs text-gray-400 mt-1">
                  {c.email ?? 'no email'}{c.source ? ` · ${c.source}` : ''}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Eligible: retention window elapsed{c.retentionUntil ? ` ${formatDate(c.retentionUntil)}` : ''} · added {formatDate(c.createdAt)}
                </p>
              </div>
              <button type="button" onClick={() => { setError(null); setTarget(c); }} className="rounded border border-red-800 text-red-300 text-xs px-3 py-1 hover:bg-red-950/40">
                Anonymise…
              </button>
            </div>
          ))}
        </div>
      )}

      {target ? (
        <ReasonModal
          open
          title={`Anonymise ${target.firstName} ${target.lastName}?`}
          description="This permanently scrubs the candidate's personal details and deletes their CV. The funnel record is kept for aggregates. This cannot be undone."
          confirmLabel="Anonymise permanently"
          confirmVariant="danger"
          isSubmitting={anonymize.isPending}
          errorMessage={error}
          onCancel={() => { setTarget(null); setError(null); }}
          onConfirm={(reason) => anonymize.mutate(reason)}
        />
      ) : null}
    </section>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
